import { FC, useEffect } from 'react';

import { useEntitySchema } from '@april9au/stack9-react';
import * as ui from '@april9au/stack9-ui';
import dayjs from 'dayjs';

import { DBBooking } from '../../models/stack9/Booking';
import { AppointmentSlotSelect } from '../AppointmentSlotSelect';

type BookingWithRelations = DBBooking & {
  event?: {
    id: number;
    event_name?: string;
    booking_type?: string;
  };
};

const Drawer = ui.S9Drawer.default;
const Form = ui.S9Form.default;
const Button = ui.S9Button.default;
const Fieldset = ui.S9Fieldset.default;
const PageSection = ui.S9PageSection.default;
const S9EntityActions = ui.S9EntityActions.default;

const { useForm, useWatch } = ui.S9Form;

interface BookingFormProps {
  id?: number;
  canEdit?: boolean;
  onSaved: () => void;
  onDeleted: () => void;
  onClose: () => void;
}

const BookingForm: FC<BookingFormProps> = ({
  id,
  canEdit = true,
  onSaved,
  onDeleted,
  onClose,
}) => {
  const [form] = useForm();
  const { schema, isValidating } = useEntitySchema('booking', id);
  const { handleSave, isLoading: isSaving } = ui.useEntityActions('booking');

  const { data: record } = ui.useScreenQueryById<BookingWithRelations>(
    'getbookingbyid',
    id,
    {
      shouldFetch: !!id,
    },
  );

  const eventIdFromForm = useWatch(['event_id'], form);
  const eventId = eventIdFromForm || record?.event_id;

  const { data: eventData } = ui.useScreenQueryById<{
    booking_type?: string;
    timezone?: string;
  }>('geteventbyid', eventId, {
    shouldFetch: !!eventId,
  });

  const bookingTypeFromEvent =
    eventData?.booking_type || record?.event?.booking_type;
  const isAppointmentEvent = bookingTypeFromEvent === 'Appointment';

  useEffect(() => {
    form.resetFields();
  }, [form, id, record]);

  useEffect(() => {
    if (!isAppointmentEvent) {
      form.setFieldValue('slot_id', null);
    }
  }, [form, isAppointmentEvent]);

  return (
    <Form
      form={form}
      disabled={isSaving || isValidating || canEdit === false}
      initialValues={record}
      loading={isValidating}
      onFinish={async values => {
        const submitData = {
          ...values,
          booking_dt: values.booking_dt
            ? dayjs(values.booking_dt).toISOString()
            : dayjs().toISOString(),
        };

        await handleSave(submitData, id);

        onSaved();
        onClose();
      }}
    >
      <Drawer.Header
        title={id ? `Edit Booking #${id}` : 'Create Booking'}
        extra={
          <>
            <Button label="Cancel" onClick={onClose} />
            <S9EntityActions
              form={form}
              entityKey="booking"
              entityId={id}
              disabled={isSaving}
              isSaving={isSaving}
              onDeleted={onDeleted}
              filterPrintableDoc={() => false}
            />
          </>
        }
      />
      <Drawer.Content>
        {schema && (
          <>
            <PageSection title="Booking Information" size="sm">
              <Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  { field: 'event_id', colSize: 24 },
                  { field: 'user_id', colSize: 24 },
                ]}
              />
              {isAppointmentEvent && (
                <AppointmentSlotSelect
                  eventId={eventId}
                  timezone={eventData?.timezone}
                  disabled={isSaving || isValidating || canEdit === false}
                />
              )}
            </PageSection>

            <PageSection title="User Details" size="sm">
              <Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  { field: 'position', colSize: 12 },
                  { field: 'department_id', colSize: 12 },
                ]}
              />
            </PageSection>

            <PageSection title="Consent & Waiver" size="sm">
              <Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  { field: 'consent_given', colSize: 12 },
                  { field: 'waiver_signed', colSize: 12 },
                ]}
              />
            </PageSection>
          </>
        )}
      </Drawer.Content>
    </Form>
  );
};

interface BookingDrawerProps {
  open: boolean;
  id?: number;
  canEdit?: boolean;
  onSaved?: () => void;
  onDeleted?: () => void;
  onClose: () => void;
}

export const BookingDrawer: FC<BookingDrawerProps> = ({
  open,
  id,
  canEdit = true,
  onSaved = () => {},
  onDeleted = () => {},
  onClose,
}) => (
  <Drawer
    open={open}
    closable
    keyboard={false}
    maskClosable={false}
    onClose={onClose}
    width={720}
  >
    <BookingForm
      id={id}
      canEdit={canEdit}
      onSaved={onSaved}
      onDeleted={onDeleted}
      onClose={onClose}
    />
  </Drawer>
);
